import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  EntityAttachment,
  CreateEntityAttachmentInput,
  UpdateEntityAttachmentInput,
} from '@peles/shared';
import * as api from '../api/entity-attachments';

export function useEntityAttachments(entityType: string, entityId: string) {
  return useQuery<EntityAttachment[]>({
    queryKey: ['entity-attachments', entityType, entityId],
    queryFn: () => api.listEntityAttachments(entityType, entityId),
    enabled: !!entityType && !!entityId,
    // A Word document is swapped for its PDF a few seconds after upload; ask
    // again until none is still on its way, so the PDF (and its preview) shows.
    refetchInterval: (query) =>
      query.state.data?.some((a) => a.pdfPending) ? PDF_PENDING_POLL_MS : false,
  });
}

/** How often a record's files are read again while a Word document converts. */
export const PDF_PENDING_POLL_MS = 5000;

/**
 * Batch-fetch attachments for many entities of the same type in one request.
 * `enabled` gates the query so callers can fetch lazily (e.g. only when the
 * itemized-list "include photos" option is turned on).
 */
export function useEntityAttachmentsForEntities(
  entityType: string,
  entityIds: string[],
  enabled = true,
) {
  // Sort ids so the query key is stable regardless of input order.
  const sortedIds = [...entityIds].sort();
  return useQuery<EntityAttachment[]>({
    queryKey: ['entity-attachments-multi', entityType, sortedIds],
    queryFn: () => api.listEntityAttachmentsForEntities(entityType, entityIds),
    enabled: enabled && !!entityType && entityIds.length > 0,
  });
}

export function useCreateEntityAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEntityAttachmentInput) => api.createEntityAttachment(input),
    onSuccess: (_, input) => {
      queryClient.invalidateQueries({
        queryKey: ['entity-attachments', input.entityType, input.entityId],
      });
    },
  });
}

export function useUpdateEntityAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateEntityAttachmentInput }) =>
      api.updateEntityAttachment(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entity-attachments'] });
    },
  });
}

export function useDeleteEntityAttachment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      entityType,
      entityId,
    }: {
      id: string;
      entityType: string;
      entityId: string;
    }) => api.deleteEntityAttachment(id, { entityType, entityId }),
    onSuccess: (_, { entityType, entityId }) => {
      queryClient.invalidateQueries({ queryKey: ['entity-attachments', entityType, entityId] });
    },
  });
}
